import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import type { Task } from '../api/types';

/**
 * Модуль хвилі 2 (облік часу, управлінський контур, юридичний контур, ШІ тощо).
 * Щоб додати модуль, створіть файл `src/features/<модуль>/routes.tsx` з типовим експортом ModuleDefinition:
 * маршрути підключаться до роутера, пункти меню з'являться, щойно масив `routes` стане непорожнім,
 * користувач матиме область доступу `scope` і (якщо задано) `useAccess()` поверне true.
 * Розширення задачі (`task`) вбудовуються в картку, рядки переліків, картки дошки й колонки списку.
 * Правок оболонки не потрібно.
 */
export type ModuleNavItem = { to: string; label: string; icon?: LucideIcon; end?: boolean };

/** Дані модуля в задачі: task.ext[<ключ модуля>] (сервер додає їх лише тим, кому вони доступні). */
export type TaskExtProps = { task: Task; ext: unknown };

/**
 * Попередження про задачу на картці дошки. Картка показує одне — найважливіше (спершу «danger», потім «warning»),
 * решту згадує лічильником: кілька кольорових позначок поруч не дають побачити головне.
 */
export type TaskAlert = { key: string; tone: 'danger' | 'warning'; label: string; detail?: string };

/** Вкладка картки задачі: «Робота» — виконання; «Управління» — управлінський шар; «Фінанси» — лише з finance:read. */
export type TaskCardTab = 'work' | 'management' | 'finance';

export type TaskExtension = {
  /**
   * Розділ у картці задачі; показується, коли в задачі є дані модуля або користувач має доступ до модуля.
   * `tab` — вкладка картки (типово «Робота»); `defaultOpen: false` — розділ другорядний і згорнутий, доки його не відкриють.
   */
  panel?: { title: string; icon?: LucideIcon; Component: ComponentType<TaskExtProps>; tab?: TaskCardTab; defaultOpen?: boolean };
  /** Значки модуля в рядку переліку й на картці дошки (лише коли є дані модуля). */
  Badges?: ComponentType<TaskExtProps & { variant: 'row' | 'card' }>;
  /** Попередження модуля для картки дошки (прострочена реакція, ризик) — замість кольорових значків на картці. */
  alerts?: (props: TaskExtProps) => TaskAlert[];
  /** Додаткові колонки подання «Список» (лише для користувачів із доступом до модуля). */
  columns?: { key: string; header: string; width: number; Cell: ComponentType<TaskExtProps> }[];
  /** Кнопки в шапці картки задачі (напр. таймер обліку часу); лише для користувачів із доступом. */
  Toolbar?: ComponentType<TaskExtProps>;
  /** Рядки в сітці реквізитів задачі (компонент повертає один чи кілька FieldRow); лише з доступом. */
  Fields?: ComponentType<TaskExtProps>;
};

/** Місця модуля в оболонці. */
export type ShellExtension = {
  /** Елемент у верхній панелі перед перемикачем теми (напр. запущений таймер). */
  Topbar?: ComponentType;
};

export type ModuleDefinition = {
  /** Ключ модуля, збігається з назвою папки (time, mgmt, legal, ai…) і з ключем у task.ext. */
  key: string;
  /** Назва розділу в навігації (українською). */
  label: string;
  icon: LucideIcon;
  /** Базова адреса модуля, напр. «/time». */
  basePath: string;
  /** Область доступу API, без якої розділ не показується (напр. «time:read»). */
  scope?: string;
  /**
   * Додаткова перевірка доступу (хук React, викликається завжди): напр. «користувач в управлінському колі».
   * Має повертати false, поки відповідь не отримано.
   */
  useAccess?: () => boolean;
  /** Порядок у навігації. */
  order?: number;
  /** Маршрути відносно basePath (дочірні до нього). Порожній масив — модуль ще не має сторінок. */
  routes: RouteObject[];
  /** Пункти підменю; якщо не задано — один пункт на basePath. */
  nav?: ModuleNavItem[];
  /** Розширення задачі. */
  task?: TaskExtension;
  /** Розширення оболонки. */
  shell?: ShellExtension;
};

const found = import.meta.glob<{ default: ModuleDefinition }>('../features/*/routes.tsx', { eager: true });

export const MODULES: ModuleDefinition[] = Object.values(found)
  .map((m) => m.default)
  .filter((m): m is ModuleDefinition => !!m && typeof m.key === 'string')
  .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

const alwaysTrue = () => true;

/**
 * Доступ до кожного модуля для поточного користувача. Порядок і кількість викликів хуків сталі:
 * перелік MODULES формується під час збирання.
 */
export function useModuleAccess(can: (scope: string) => boolean): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const m of MODULES) {
    const access = (m.useAccess ?? alwaysTrue)();
    map.set(m.key, access && (!m.scope || can(m.scope)));
  }
  return map;
}

/** Модулі, що мають сторінки й доступні користувачу (для меню й панелі команд). */
export function useVisibleModules(can: (scope: string) => boolean): ModuleDefinition[] {
  const access = useModuleAccess(can);
  return MODULES.filter((m) => m.routes.length > 0 && access.get(m.key));
}

/** Дані модуля в задачі (task.ext — розширення за назвою модуля). */
export function taskExt(task: Task, key: string): unknown {
  const ext = (task as Task & { ext?: Record<string, unknown> }).ext;
  return ext ? ext[key] : undefined;
}
